import type { ColumnDef } from "@tanstack/react-table";
import Table, { TableHeading } from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import { toFormat } from "@/lib/dateHelpers";
import type { ActiveYearsBandRow } from "./activeYearsBreakdown";
import { formatYearsAndMonths } from "./activeYearsBreakdown";

type ActiveYearsBandTableRow = {
  id: string;
  section: string;
  classification: string;
  startDate: string;
  endDate: string;
  contribution: string;
};

const columns: ColumnDef<ActiveYearsBandTableRow>[] = [
  { header: "Section", id: "section", accessorKey: "section", size: 280, minSize: 200 },
  {
    header: "Class",
    id: "classification",
    accessorKey: "classification",
    size: 72,
    minSize: 64
  },
  { header: "Start Date", id: "startDate", accessorKey: "startDate", size: 120 },
  { header: "End Date", id: "endDate", accessorKey: "endDate", size: 120 },
  { header: "Contribution", id: "contribution", accessorKey: "contribution", size: 110 }
];

type Props = {
  bandRows: ActiveYearsBandRow[];
  loading?: boolean;
};

export default function ActiveYearsBandsTable({ bandRows, loading = false }: Props) {
  const data = bandRows.map((row) => ({
    id: row.id,
    section: row.section ?? "—",
    classification: row.classification ?? "—",
    startDate: toFormat(row.startedOn) ?? "—",
    endDate: row.ongoing ? "Today" : (toFormat(row.endedOn) ?? "—"),
    contribution: formatYearsAndMonths(row.days)
  }));

  return (
    <div data-testid="active-years-breakdown-table">
      <Table
        data={data}
        columns={columns}
        loading={loading}
        renderDetails={() => (
          <TableHeading
            body="Total comes straight from CCMS. The table below explains how the per-band math contributes to the total."
            topSpacing="none"
          />
        )}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No Member Bands"
            caption="No member bands available to contribute to the total"
            iconName="person"
          />
        )}
      />
    </div>
  );
}
